<template>
  <button
    type="button"
    class="instagram-feed__tile"
    :class="[tileClass, { 'instagram-feed__tile--hoverable': hoverEnabled }]"
    :style="tileStyle"
    @click.stop="$emit('open', post)"
  >
    <video
      v-if="playVideo"
      class="instagram-feed__image"
      :src="post.mediaUrl"
      :poster="imageSrc"
      autoplay
      muted
      loop
      playsinline
    ></video>
    <img
      v-else
      class="instagram-feed__image"
      :src="imageSrc"
      :alt="post.caption || 'Instagram post'"
      loading="lazy"
    />

    <span v-if="mediaIconKind" class="instagram-feed__media-icon" aria-hidden="true">
      <svg v-if="mediaIconKind === 'video'" viewBox="0 0 24 24" width="14" height="14">
        <path fill="currentColor" d="M8 5v14l11-7z"/>
      </svg>
      <svg v-else viewBox="0 0 24 24" width="14" height="14">
        <rect x="3" y="5" width="12" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="2"/>
        <rect x="9" y="7" width="12" height="12" rx="2" fill="currentColor" opacity="0.9"/>
      </svg>
    </span>

    <div v-if="showOverlay" class="instagram-feed__engagement" :style="engagementStyle">
      <span v-if="showHoverLogo" class="instagram-feed__overlay-logo" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16">
          <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" stroke-width="2"/>
          <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" stroke-width="2"/>
          <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor"/>
        </svg>
      </span>
      <span v-if="showLikes" :style="likesIconStyle">♥ {{ formatCount(post.likeCount) }}</span>
      <span v-if="showComments" :style="commentsIconStyle">💬 {{ formatCount(post.commentsCount) }}</span>
      <p v-if="showHoverCaption" class="instagram-feed__caption">{{ post.caption }}</p>
    </div>
  </button>
</template>

<script>
export default {
  name: 'InstagramFeedPostTile',
  props: {
    post: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      default() {
        return {};
      },
    },
    tileStyle: {
      type: Object,
      default() {
        return {};
      },
    },
    tileClass: {
      type: String,
      default: '',
    },
    engagementStyle: {
      type: Object,
      default() {
        return {};
      },
    },
    likesIconStyle: {
      type: Object,
      default() {
        return {};
      },
    },
    commentsIconStyle: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  computed: {
    hoverEnabled() {
      const c = this.config || {};
      if (typeof c.hoverEffectsEnabled !== 'undefined') {
        return !!c.hoverEffectsEnabled;
      }
      return c.showEngagement !== false;
    },
    showLogo() {
      return this.config.showInstagramLogo !== false;
    },
    showLikes() {
      return this.config.showLikes !== false;
    },
    showComments() {
      return this.config.showComments !== false;
    },
    showCaption() {
      return this.config.showCaption !== false;
    },
    showHoverLogo() {
      return this.hoverEnabled && this.showLogo;
    },
    showHoverCaption() {
      return this.hoverEnabled && this.showCaption && !!(this.post && this.post.caption);
    },
    showOverlay() {
      return this.showHoverLogo || this.showLikes || this.showComments || this.showHoverCaption;
    },
    isVideo() {
      const type = this.post && this.post.mediaType;
      return type === 'VIDEO' || type === 'REELS';
    },
    isCarousel() {
      return this.post && this.post.mediaType === 'CAROUSEL_ALBUM';
    },
    playVideo() {
      return this.config.autoplayVideos !== false && this.isVideo && !!(this.post && this.post.mediaUrl);
    },
    mediaIconKind() {
      if (this.config.showMediaIcon === false) {
        return '';
      }
      if (this.isVideo) {
        return 'video';
      }
      if (this.isCarousel) {
        return 'carousel';
      }
      return '';
    },
    imageSrc() {
      const post = this.post || {};
      if (this.isVideo) {
        return post.thumbnailUrl || post.mediaUrl;
      }
      return post.mediaUrl || post.thumbnailUrl;
    },
  },
  methods: {
    formatCount(value) {
      const n = Number(value) || 0;
      if (n >= 1000000) {
        return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      }
      if (n >= 1000) {
        return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
      }
      return String(n);
    },
  },
};
</script>

<style scoped>
.instagram-feed__tile {
  position: relative;
  display: block;
  overflow: hidden;
  background: #f0f0f0;
  text-decoration: none;
  color: #fff;
  border: 0;
  padding: 0;
  margin: 0;
  width: 100%;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}
.instagram-feed__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.instagram-feed__media-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.instagram-feed__engagement {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.55));
  z-index: 1;
}
.instagram-feed__tile--hoverable .instagram-feed__engagement {
  opacity: 0;
  transition: opacity 0.18s ease;
}
.instagram-feed__tile--hoverable:hover .instagram-feed__engagement {
  opacity: 1;
}
.instagram-feed__overlay-logo {
  display: inline-flex;
  align-items: center;
}
.instagram-feed__caption {
  flex: 1 0 100%;
  margin: 0;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.35;
  max-height: 2.7em;
  overflow: hidden;
  text-align: left;
}
.instagram-feed__tile--masonry {
  break-inside: avoid;
  display: inline-block;
}
</style>



// WEBPACK FOOTER //
// src/components/editor/apps/InstagramFeed/FeedPostTile.vue